import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  UserCircle, MonitorCheck, Users, Headphones, ArrowUpRight, Asterisk, ArrowRight, Phone,
} from "lucide-react";
import { MaskedLines, Reveal, Overline } from "@/components/Reveal";
import { GoldButton, OutlineButton } from "@/components/Brand";
import { useEnquiry } from "@/components/EnquiryDialog";
import { IMAGES, FEATURES, PROGRAMMES, TRAINERS } from "@/lib/data";

const featureIcons = { UserCircle, MonitorCheck, Users, Headphones };

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[680px] overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-y-0 right-0 w-full lg:w-[58%]">
        <img src={IMAGES.heroModel} alt="Aesthetics treatment" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(10,20,32,1) 0%, rgba(10,20,32,0.9) 22%, rgba(10,20,32,0.5) 55%, rgba(10,20,32,0.35) 100%)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(10,20,32,0.85) 0%, rgba(10,20,32,0) 55%)" }} />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-start pt-40 md:pt-52">
        <div className="max-w-2xl">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}>
          <Overline className="mb-6 block">The Aesthetics Academy Co</Overline>
        </motion.div>
        <MaskedLines
          className="font-playfair uppercase leading-[0.9] tracking-tight"
          lineClassName="text-[clamp(2.75rem,10vw,6.75rem)]"
          lines={[
            <span key="1" className="text-gold">Advancing</span>,
            <span key="2" className="text-gold">Skills.</span>,
            <span key="3" className="text-white">Elevating</span>,
            <span key="4" className="text-white">Careers.</span>,
          ]}
          stagger={0.1}
        />
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-md text-lg md:text-xl text-white/70 font-manrope"
        >
          Professional training in aesthetics designed around you.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-10"
        >
          <Link to="/courses">
            <GoldButton data-testid="hero-explore-button">Explore Courses <ArrowRight size={16} strokeWidth={2} /></GoldButton>
          </Link>
        </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/40"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
      </motion.div>
    </section>
  );
};

const FeatureStrip = () => (
  <section className="relative z-10 bg-navy-surface border-y border-white/10">
    <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-white/10">
      {FEATURES.map((f, i) => {
        const Icon = featureIcons[f.icon];
        return (
          <Reveal key={f.title} delay={i * 0.1} className="p-8 lg:p-10 group">
            <Icon size={38} strokeWidth={1} className="text-gold mb-5" />
            <h3 className="font-manrope text-sm tracking-[0.15em] uppercase text-white mb-3">{f.title}</h3>
            <p className="text-white/50 text-sm leading-relaxed">{f.text}</p>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const MarqueeStrip = () => (
  <section className="py-10 bg-navy border-b border-white/10 overflow-hidden">
    <Marquee speed={45} gradient={false} autoFill>
      {["Advancing Skills", "Elevating Careers", "Safe Practice", "Professional Standards"].map((t) => (
        <span key={t} className="flex items-center">
          <span className="font-playfair text-4xl md:text-6xl text-white/90 px-8">{t}</span>
          <Asterisk className="text-gold" size={34} strokeWidth={1.2} />
        </span>
      ))}
    </Marquee>
  </section>
);

const CoursesPreview = () => (
  <section className="py-28 md:py-36 bg-navy-surface border-y border-white/10">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-6">
        <Reveal>
          <Overline>Our Courses</Overline>
          <h2 className="font-playfair text-4xl md:text-6xl text-white mt-4 leading-tight">Master your craft</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Link to="/courses" className="inline-flex items-center gap-2 text-gold group text-sm tracking-[0.15em] uppercase">
            View all courses
            <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </Link>
        </Reveal>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PROGRAMMES.map((p, i) => (
          <Reveal key={p.id} delay={(i % 3) * 0.12}>
            <Link to={p.path} className="group relative block h-80 md:h-[420px] overflow-hidden">
              <img src={p.image} alt={p.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
              <div className="absolute inset-0 bg-navy/60" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/85 to-navy/20" />
              <div className="relative h-full p-7 md:p-8 flex flex-col justify-end">
                <span className="text-[11px] tracking-[0.2em] uppercase text-gold mb-3">{p.tagline}</span>
                <h3 className="font-playfair text-2xl md:text-3xl text-white group-hover:text-gold transition-colors leading-tight">{p.name}</h3>
                <p className="text-white/65 mt-3 text-sm">{p.blurb}</p>
                <span className="inline-flex items-center gap-2 mt-5 text-gold text-xs tracking-[0.15em] uppercase">
                  {p.ctaLabel ? p.ctaLabel(p.count) : `${p.count} Courses`}
                  <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const chapters = [
  { no: "01", title: "The Mission", text: "A modern training academy committed to excellence, safety and real results, supporting practitioners at every stage of their professional development." },
  { no: "02", title: "Hands-On",    text: "Practical learning with live models in a supportive, professional environment where confidence is built through real experience." },
  { no: "03", title: "The Support", text: "We're with you every step of the way, from your first day of training to long after your course is complete." },
];

const Manifesto = () => (
  <section className="py-28 md:py-40 bg-navy">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <Reveal className="mb-20">
        <Overline>Why The Academy</Overline>
        <h2 className="font-playfair text-4xl md:text-6xl text-white mt-4 max-w-3xl leading-tight">
          Education built on experience. Training built on trust.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
        {chapters.map((c, i) => (
          <Reveal key={c.no} delay={i * 0.12} className="group">
            <div className="flex items-baseline gap-4 mb-6">
              <span className="font-playfair text-7xl md:text-8xl text-gold/25 group-hover:text-gold/50 transition-colors duration-500">{c.no}</span>
              <span className="hairline flex-1 mb-4" />
            </div>
            <h3 className="font-playfair text-2xl md:text-3xl text-white mb-4">{c.title}</h3>
            <p className="text-white/55 leading-relaxed">{c.text}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TrainersPreview = () => (
  <section className="py-28 md:py-36 bg-navy">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
      <Reveal className="mb-16 text-center">
        <Overline>Meet Your Trainers</Overline>
        <h2 className="font-playfair text-4xl md:text-6xl text-white mt-4">Learn from experienced practitioners and educators</h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
        {TRAINERS.map((t, i) => (
          <Reveal key={t.name} delay={i * 0.15}>
            <div className="group relative overflow-hidden">
              <div className="relative h-[440px] overflow-hidden">
                <img
                  src={t.image}
                  alt={t.name}
                  className={`w-full h-full object-cover transition-transform duration-[1.2s] group-hover:scale-105 ${
                    t.name === "Charlotte Thompson" ? "object-[center_25%]" : "object-center"
                  }`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
              </div>
              <div className="absolute bottom-0 inset-x-0 p-8">
                <h3 className="font-playfair text-3xl text-white">{t.name}</h3>
                <div className="w-12 h-px bg-gold mt-3 mb-3" />
                <p className="text-gold text-[11px] tracking-[0.2em] uppercase mb-2">{t.title}</p>
                {t.phone && (
                  <a
                    href={`tel:${t.phone.replace(/\s/g, "")}`}
                    data-testid={`home-trainer-phone-${t.name.split(" ")[0].toLowerCase()}`}
                    className="inline-flex items-center gap-2 text-white/70 hover:text-gold transition-colors text-sm mt-1"
                  >
                    <Phone size={14} strokeWidth={1.5} />
                    {t.phone}
                  </a>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const FinalCTA = () => {
  const { openEnquiry } = useEnquiry();
  return (
    <section className="relative py-32 md:py-44 overflow-hidden">
      <div className="absolute inset-0">
        <img src={IMAGES.salon} alt="Academy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/90" />
      </div>
      <div className="relative max-w-3xl mx-auto px-6 text-center">
        <Reveal>
          <Overline>More Than A Training Academy</Overline>
          <h2 className="font-playfair text-4xl md:text-6xl text-white mt-6 leading-tight">
            A place to learn, grow and be supported.
          </h2>
          <p className="text-white/60 mt-6 text-lg max-w-xl mx-auto">
            Whether you&apos;re taking your first steps into aesthetics or developing your existing skills, we&apos;re here to support you throughout your training and professional development.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-10">
            <Link to="/courses"><GoldButton data-testid="cta-explore-button">Explore Courses</GoldButton></Link>
            <OutlineButton onClick={() => openEnquiry()} data-testid="cta-enquire-button">Get In Touch</OutlineButton>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <>
      <Hero />
      <FeatureStrip />
      <MarqueeStrip />
      <CoursesPreview />
      <Manifesto />
      <TrainersPreview />
      <FinalCTA />
    </>
  );
}
